import graphql from "babel-plugin-relay/macro";
import { useMutation } from "react-relay";

import { PatronBookingRateChangeConfirmModalRateInitializationData } from "@/features/PatronBookingRateChangeConfirmModal/PatronBookingRateChangeConfirmModal";
import { useFlags } from "@/utils/launchDarkly/useFlags";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import {
  BookingType,
  UpdateBookingSlotInput,
  useUpdateSlotMutation,
} from "./__generated__/useUpdateSlotMutation.graphql";

export const useUpdateSlot = (
  setIsRateChangeConfirmModalOpen: (isOpen: boolean) => void,
  initializeRateChangeConfirmModal: (
    rateUpdates: PatronBookingRateChangeConfirmModalRateInitializationData
  ) => void
) => {
  const { showToast } = useToast();
  const { patronLoyalty } = useFlags();

  const [updateSlot, isUpdatingSlot] = useMutation<useUpdateSlotMutation>(
    useUpdateSlotMutationString
  );

  const onCompleted = (res: any, successCallback: any) => {
    if (!!res.responsePayload?.success) {
      if (!!successCallback) {
        successCallback();
      }
    }
  };

  const handleUpdateSlot = (
    input: UpdateBookingSlotInput,
    membershipTypeId: string,
    bookingType: BookingType,
    successCallback?: () => void
  ) => {
    updateSlot({
      variables: {
        input,
        membershipTypeId,
        bookingType,
        patronLoyalty: !!patronLoyalty,
      },
      onCompleted: (res) => {
        if (res.responsePayload?.success) {
          onCompleted(res, successCallback);
        } else if (
          res.responsePayload?.errors?.length &&
          res.responsePayload?.errors?.length > 0
        ) {
          const rateUpdateErrors = res.responsePayload?.errors?.filter(
            (err: any) => err.code === "rate_update"
          );
          if (rateUpdateErrors?.length && rateUpdateErrors?.length > 0) {
            setIsRateChangeConfirmModalOpen(true);
            initializeRateChangeConfirmModal({
              bookingId: input.bookingId,
              rateUpdates: rateUpdateErrors?.flatMap(
                (err: any) =>
                  err.rateUpdates?.map((rateUpdate: any) => ({
                    targetId: rateUpdate.targetId,
                    rateId: rateUpdate.rateId,
                    slotTargetId: input.startSlotId,
                  })) ?? []
              ),
              confirmCallback: () => {
                const roundRateApprovals = rateUpdateErrors?.flatMap(
                  (err: any) =>
                    err.rateUpdates?.map((rateUpdate: any) => ({
                      roundId: rateUpdate.targetId,
                      acceptRateUpdate: true,
                    })) ?? []
                );
                updateSlot({
                  variables: {
                    input: {
                      ...input,
                      bookingRateApproval: {
                        acceptRateUpdate: true,
                        bookingId: input.bookingId,
                        roundRateApprovals,
                      },
                    },
                    membershipTypeId,
                    bookingType,
                    patronLoyalty: !!patronLoyalty,
                  },
                  onCompleted: (res: any) => {
                    if (res.responsePayload?.success) {
                      onCompleted(res, successCallback);
                    } else {
                      //throw toast error
                      res.responsePayload?.errors.forEach(
                        (error: any, index: number) => {
                          showToast({
                            description: error.message || error.code,
                            semanticStyle: "error",
                            toastId: `tee-time-error-${index}`,
                            testID: `lock-slot-error-toast-${index}`,
                          });
                        }
                      );
                    }
                  },
                });
              },
            });
          }
        }
      },
    });
  };

  return [handleUpdateSlot, isUpdatingSlot] as const;
};

export const useUpdateSlotMutationString = graphql`
  mutation useUpdateSlotMutation(
    $input: UpdateBookingSlotInput!
    $membershipTypeId: ID!
    $bookingType: BookingType!
    $patronLoyalty: Boolean!
  ) {
    responsePayload: updateBookingSlot(input: $input) {
      errors {
        field
        code
        ... on RateUpdateError {
          rateUpdates {
            targetId
            rateId
          }
        }
      }
      success
      source {
        id
      }
      target {
        id
        ... on TimeSlot {
          agenda {
            date
          }
          time
          bookings {
            edges {
              node {
                id
                startSlot {
                  id
                }
                ...BookingSummary_booking
                  @arguments(patronLoyalty: $patronLoyalty)
              }
            }
          }
        }
      }
    }
  }
`;
